import { CommonModule } from '@angular/common';
import { Component, input, signal, ViewChild } from '@angular/core';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwDialogComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  DialogOptions,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';

import { AwBreadCrumbComponent } from '@assetworks-llc/aw-component-lib';

import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { Router } from '@angular/router';
import {
  CreateEmployeeRequest,
} from '../../../../../api/models';

import { catchError, finalize, of } from 'rxjs';
import { EmployeeService } from '../../../../../core/services/employee.service';

@Component({
  selector: 'app-add-customer',
  imports: [
    AwDividerComponent,
    AwBreadCrumbComponent,
    AwTextFormComponent,
    CommonModule,
    AwSpinnerComponent,
    AwActionBarComponent,
    AwDialogComponent,
    AwToastComponent,
  ],
  templateUrl: './add-employee.component.html',
  styleUrls: [
    './add-employee.component.scss',
    '../../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class AddEmployeeComponent {
  constructor(
    private router: Router,
    private employeeService: EmployeeService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  employeeNameInput: string = '';
  employeeEmailInput: string = '';
  organizationIdInput: string = '';

  openEmployeeInfoDialog: boolean = false;

  dialogOptions: DialogOptions = {
    variant: 'alert',
    title: 'Employee Created',
    description: 'Employee has been successfully created.',
    primaryButtonLabel: 'Close',
  };

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Employee Management',
      link: '/employee-management/overview',
      newTab: false,
    },
    {
      label: 'New Employee',
      route: '/employee-management/add-employee',
      newTab: false,
    },
  ]);

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate(['/employee-management']),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => this.createEmployee(),
      },
    },
  ]);

  onEmployeeNameSelected(text: string | null) {
    this.employeeNameInput = text || '';
  }

  onEmployeeEmailSelected(text: string | null) {
    this.employeeEmailInput = text || '';
  }

  onOrganizationIDSelected(text: string | null) {
    this.organizationIdInput = text || '';
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateEmployeeFields(): boolean {
    return (
      this.employeeNameInput !== '' &&
      this.employeeNameInput !== null &&
      this.employeeEmailInput !== '' &&
      this.employeeEmailInput !== null &&
      this.organizationIdInput !== '' &&
      this.organizationIdInput !== null
    );
  }

  createEmployee(): void {
    if (!this.validateEmployeeFields()) {
      this.buildToastMessage('Please fill in all fields.', 'error');
      return;
    }

    this.isLoading = true;

    const newEmployee: CreateEmployeeRequest = {
      emailAddress: this.employeeEmailInput,
      name: this.employeeNameInput,
    };

    this.employeeService
      .createEmployee({
        organizationId: this.organizationIdInput,
        body: newEmployee,
      })
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed to create employee', 'error');
          return of(null);
        })
      )
      .subscribe((employee) => {
        if (employee) {
          this.openEmployeeInfoDialog = true;
          this.buildToastMessage('Successfully created employee', 'success');
        } else {
          this.buildToastMessage('Failed to create employee', 'error');
        }
      });
  }

  dialogCloseHandler() {
    this.openEmployeeInfoDialog = false;
    this.router.navigate(['/employee-management/overview']).then(() => {
      window.location.reload();
    });
  }
}
